/**
 * Tela de gerenciamento de administradores.
 * Permite adicionar e remover admins (exceto Super Admins).
 * Acessível apenas para usuários admin via menu.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, UserPlus, Trash2, ArrowLeft, AlertCircle, BookOpen } from 'lucide-react';
import { useAdmin } from '@/contexts/AdminContext';
import { useToast } from '@/hooks/useToast';
import AdminDocViewer from './AdminDocViewer';

export default function AdminManagement({ onBack }) {
  const { t } = useTranslation();
  const { adminList, isCurrentUserAdmin, isCurrentUserSuperAdmin, addAdmin, removeAdmin, loading } = useAdmin();
  const { success, error: showError } = useToast();

  const [adminView, setAdminView] = useState('admins'); // 'admins' | 'docs'
  const [newEmail, setNewEmail] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null);

  if (!isCurrentUserAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-indigo-900 py-4">
        <div className="w-full max-w-full mx-auto pt-8">
          <div className="bg-red-500/10 border border-red-500 rounded-lg p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">
              {t('admin.accessDenied') || 'Acesso negado'}
            </h2>
            <p className="text-gray-300">
              {t('admin.adminOnly') || 'Apenas administradores podem acessar esta página'}
            </p>
            {onBack && (
              <button
                onClick={onBack}
                className="mt-4 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white"
              >
                <ArrowLeft className="inline w-4 h-4 mr-2" />
                {t('common.back') || 'Voltar'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    
    if (!newEmail.trim()) {
      showError(t('admin.emailRequired') || 'E-mail é obrigatório');
      return;
    }

    setAdding(true);
    try {
      await addAdmin(newEmail.trim(), newDisplayName.trim());
      success(t('admin.adminAdded') || 'Admin adicionado com sucesso');
      setNewEmail('');
      setNewDisplayName('');
    } catch (err) {
      showError(err.message || t('admin.addError') || 'Erro ao adicionar admin');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveAdmin = async (email) => {
    if (!confirm(t('admin.confirmRemove', { email }) || `Confirma remover ${email}?`)) {
      return;
    }

    setRemoving(email);
    try {
      await removeAdmin(email);
      success(t('admin.adminRemoved') || 'Admin removido com sucesso');
    } catch (err) {
      showError(err.message || t('admin.removeError') || 'Erro ao remover admin');
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-indigo-900 py-4 flex flex-col">
      <div className="w-full max-w-full mx-auto flex flex-col flex-1 min-h-0">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 pt-4 flex-shrink-0">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="flex items-center justify-center p-1 text-gray-300 hover:text-white transition-colors rounded"
              aria-label={t('common.back') || 'Voltar'}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-purple-400" />
            <h1 className="text-2xl font-bold text-white">
              {t('admin.title') || 'Administrativo'}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setAdminView('admins')}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${adminView === 'admins' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white hover:bg-purple-500/20'}`}
            >
              <Shield className="w-4 h-4" />
              {t('admin.adminsTab') || 'Administrativo'}
            </button>
            <button
              type="button"
              onClick={() => setAdminView('docs')}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${adminView === 'docs' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white hover:bg-purple-500/20'}`}
            >
              <BookOpen className="w-4 h-4" />
              {t('admin.docsTab') || 'Documentação'}
            </button>
          </div>
        </div>

        {adminView === 'docs' ? (
          <div
            className="flex flex-col min-h-0 flex-1"
            style={{ maxHeight: 'calc(100vh - 14rem)' }}
          >
            <AdminDocViewer onBack={onBack} />
          </div>
        ) : (
          <>
        {/* Formulário de adição */}
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg border border-purple-500/20 p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-purple-400" />
            {t('admin.addNewAdmin') || 'Adicionar novo administrador'}
          </h2>
          <form onSubmit={handleAddAdmin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('admin.emailLabel') || 'E-mail'} *
              </label>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="admin@example.com"
                className="w-full px-4 py-2 bg-gray-900/50 border border-purple-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                disabled={adding}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('admin.displayNameLabel') || 'Nome para exibição'} ({t('common.optional') || 'opcional'})
              </label>
              <input
                type="text"
                value={newDisplayName}
                onChange={(e) => setNewDisplayName(e.target.value)}
                placeholder={t('admin.displayNamePlaceholder') || 'Nome do administrador'}
                className="w-full px-4 py-2 bg-gray-900/50 border border-purple-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                disabled={adding}
              />
            </div>
            <button
              type="submit"
              disabled={adding || !newEmail.trim()}
              className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors"
            >
              {adding ? t('common.loading') || 'Adicionando...' : t('admin.addButton') || 'Adicionar admin'}
            </button>
          </form>
        </div>

        {/* Lista de admins */}
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg border border-purple-500/20 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">
            {t('admin.adminList') || 'Administradores'} ({adminList.length})
          </h2>
          
          {loading ? (
            <div className="text-center py-8 text-gray-400">
              {t('common.loading') || 'Carregando...'}
            </div>
          ) : adminList.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              {t('admin.noAdmins') || 'Nenhum administrador cadastrado'}
            </div>
          ) : (
            <div className="space-y-3">
              {adminList.map((admin) => (
                <div
                  key={admin.email}
                  className="flex items-center justify-between p-4 bg-gray-900/30 rounded-lg border border-purple-500/10"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium">{admin.email}</span>
                      {admin.isSuperAdmin && (
                        <span className="px-2 py-0.5 bg-yellow-500/20 border border-yellow-500/50 rounded text-xs text-yellow-300 font-semibold">
                          {t('admin.superAdmin') || 'Super Admin'}
                        </span>
                      )}
                    </div>
                    {admin.displayName && (
                      <div className="text-sm text-gray-400 mt-1">{admin.displayName}</div>
                    )}
                    {admin.addedBy && admin.addedBy !== 'env' && (
                      <div className="text-xs text-gray-500 mt-1">
                        {t('admin.addedBy') || 'Adicionado por'}: {admin.addedBy}
                      </div>
                    )}
                  </div>
                  <div>
                    {admin.isSuperAdmin ? (
                      <div className="text-xs text-gray-500 italic">
                        {t('admin.cannotRemove') || 'Não removível'}
                      </div>
                    ) : (
                      <button
                        onClick={() => handleRemoveAdmin(admin.email)}
                        disabled={removing === admin.email}
                        className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title={t('admin.removeButton') || 'Remover admin'}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info adicional */}
        {isCurrentUserSuperAdmin && (
          <div className="mt-6 bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <div className="text-sm text-blue-300">
              <strong>{t('admin.infoTitle') || 'Informação'}:</strong>{' '}
              {t('admin.superAdminInfo') || 'Super Admins são definidos no arquivo .env.local (VITE_SUPER_ADMIN_EMAILS) e não podem ser removidos pela interface.'}
            </div>
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
}
